import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { FullPageLoader, MDBox, Chart, MDTypography } from '@components';

import { RootState } from '@store/store';
import { useUpdateChartSvgMutation } from '@api/chart';
import { ChartConfigType } from '@DStypes/chart';

import { am4core } from '@utils/chart';
import { splitIntoChunk } from '@utils/common';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  numberEquivalents
} from '@constants/common';

interface Props {
  chartConfigs: ChartConfigType[];
  projectColorSet?: string[];
  projectId: string;
  onComplete: (charts?: string[]) => void;
}

const ChartSvgUpload: React.FC<Props> = ({
  chartConfigs,
  projectColorSet,
  projectId,
  onComplete
}) => {
  const chartDataObj = useSelector(
    (state: RootState) => state?.projectBoard.data
  );
  const [updateSvgArr] = useUpdateChartSvgMutation();

  const [chartImageArr, setChartImageArr] = useState<
    { chart_id: string; svg: string }[]
  >([]);

  const filteredChartConfig = useMemo<ChartConfigType[]>(
    () => chartConfigs.filter((item: ChartConfigType) => !item.svg_generated),
    [chartConfigs]
  );

  const charts = useMemo<JSX.Element[]>(
    () =>
      filteredChartConfig.map((chartConfig: ChartConfigType) => {
        let colorSet;

        if (!chartConfig?.config?.visual_settings?.theme_connection_status)
          colorSet = projectColorSet || null;
        else if (
          chartConfig?.config?.visual_settings?.theme_connection_status
            ?.colorThemeToggle
        )
          colorSet = projectColorSet || null;
        else if (chartConfig?.config?.visual_settings?.colorThemeToggle)
          colorSet = chartConfig?.colorset || null;
        else colorSet = null;

        const chartConfigJSON = JSON.parse(chartConfig.config.chart_config);
        const isSelfChart =
          chartConfig?.config?.x_axis?.id === chartConfig?.config?.y_axis?.id;

        const isNumber = numberEquivalents.includes(
          chartConfig?.config?.x_axis?.treat_as_type as string
        );

        const isYPercentage =
          chartConfig?.config?.y_axis?.treat_as_type === 'PERCENTAGE'
            ? true
            : false;

        const isXPercentage =
          chartConfig?.config?.x_axis?.treat_as_type === 'PERCENTAGE'
            ? true
            : false;

        const xAxisType = chartConfig?.config?.x_axis?.treat_as_type;

        const xAxisField = chartConfig?.config?.x_axis?.fieldname || '';

        const activeDateGroup =
          chartConfig?.config.date_group === 'none'
            ? xAxisType === 'DATE'
              ? DATE_GROUP.DAYS
              : DATE_TIME_GROUP.HOURS
            : chartConfig?.config.date_group;

        return (
          chartDataObj?.[chartConfig.id]?.data && (
            <Chart
              key={chartConfig.id}
              chartId={chartConfig.id}
              data={chartDataObj[chartConfig.id].data}
              chartConfig={{
                ...chartConfigJSON,
                legend: { ...chartConfigJSON.legend, scrollable: false }
              }}
              chartType={chartConfig.config.chart_type}
              height='378px'
              width='642.5px'
              visualSettings={chartConfig.config.visual_settings}
              isSegmented={chartConfig.config.segment_field}
              getSvg={(svg: string, legend_svg?: string) =>
                setChartImageArr((prev) => [
                  ...prev,
                  {
                    chart_id: chartConfig.id,
                    svg,
                    ...(legend_svg && { legend_svg })
                  }
                ])
              }
              isPreview
              colorSet={colorSet}
              dateGroup={activeDateGroup}
              isDate={chartConfig.config?.x_axis?.treat_as_type === 'DATE'}
              xDateItemsToRender={30}
              isSelfChart={isSelfChart}
              isNumber={isNumber}
              chartOperation={chartConfig.config.chart_operation}
              isYPercentage={isYPercentage}
              isXPercentage={isXPercentage}
              skipEmptyPeriods={chartConfig?.config?.date_group === 'none'}
              xAxisField={xAxisField}
            />
          )
        );
      }),
    [filteredChartConfig, chartDataObj]
  );

  useEffect(() => {
    if (filteredChartConfig.length === chartImageArr.length && projectId)
      if (filteredChartConfig.length) {
        const updateSvgPromises = splitIntoChunk(chartImageArr, 5).map(
          (chunkArr: any[]) =>
            updateSvgArr({ id: projectId, chart_svg_list: chunkArr })
        );

        Promise.all(updateSvgPromises).then(() =>
          onComplete(
            filteredChartConfig.map((config: ChartConfigType) => config.id)
          )
        );
      } else {
        onComplete();
      }
  }, [filteredChartConfig, chartImageArr]);

  useEffect(
    () => () => {
      am4core.registry.deferred = am4core.registry.deferred.filter((config) => {
        if (!config.scope.includes('-preview')) return true;
      });
      am4core.registry.queue = am4core.registry.queue.filter(
        (config) => !config.id.includes('-preview')
      );
    },
    []
  );

  return (
    <>
      <MDBox
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        position='relative'
        sx={{ height: '100%', width: '100%', overflow: 'hidden' }}
      >
        <MDTypography
          variant='h5'
          color='info'
          textAlign='center'
          sx={{ mb: 2 }}
          te
        >
          Processing your request.
        </MDTypography>
        <MDTypography
          variant='h5'
          color='info'
          textAlign='center'
          sx={{ mb: 2 }}
        >
          This will take a few seconds. Please do not close or refresh your page
        </MDTypography>
        <FullPageLoader height='200px' />
        <MDBox
          sx={{ position: 'absolute', opacity: '0', pointerEvents: 'none' }}
        >
          {charts}
        </MDBox>
      </MDBox>
    </>
  );
};

export default ChartSvgUpload;
